import React, { useMemo } from 'react';
import { Animated, StyleSheet, View, Platform } from 'react-native';
import { CHAT_INPUT_STYLE, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import { useUIStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTheme } from '@/context/ThemeContext';

const createStyles = (isXS, chatInputStyle, messageWidth, colors) => {
  const customStyles = {
    inputContainer: {
      paddingHorizontal: 15,
      height: 'auto',
    },
    inputWrapper: {
      maxWidth: '100%',
      paddingBottom: 0,
      height: 'auto',
    },
  };

  if (chatInputStyle === CHAT_INPUT_STYLE.DETAILED) {
    customStyles.inputContainer.paddingHorizontal = 0;
    customStyles.inputContainer.height = '100%';
    customStyles.inputWrapper.height = '100%';
  }

  if (chatInputStyle === CHAT_INPUT_STYLE.FIXED) {
    customStyles.inputContainer.paddingHorizontal = 0;
  }

  if (chatInputStyle === CHAT_INPUT_STYLE.FLOATING) {
    if (messageWidth === MESSAGE_WIDTH.NARROW) {
      customStyles.inputWrapper.maxWidth = MESSAGE_WIDTH_AMOUNTS.NARROW;
    } else if (messageWidth === MESSAGE_WIDTH.MEDIUM) {
      customStyles.inputWrapper.maxWidth = MESSAGE_WIDTH_AMOUNTS.MEDIUM;
    } 
  }

  return StyleSheet.create({
    inputContainer: {
      width: '100%',
      position: 'relative',
      ...(Platform.OS === 'web' ? { boxSizing: 'border-box' } : {}),
      alignItems: 'center',
      backgroundColor: isXS ? colors.solid_color_primary : 'transparent',
      zIndex: 10,
      bottom: 0,
      left: 0,
      right: 0,
      paddingHorizontal: customStyles.inputContainer.paddingHorizontal,
      height: customStyles.inputContainer.height,
    },
    inputWrapper: {
      width: '100%',
      maxWidth: customStyles.inputWrapper.maxWidth,
      height: customStyles.inputWrapper.height,
      paddingVertical: 0,
    },
  });
};

/**
 * @param {Object} [wrapperAnimatedStyle] Animated style applied to the input
 *   wrapper itself. Used by the chat → Work handover to fade the composer
 *   through zero while its controls change (see useWorkModeHandover). It goes
 *   on the existing wrapper rather than a new node so the DETAILED layout,
 *   which relies on this View filling its parent, is unaffected.
 */
function ChatInputContainer({
  children,
  forceInputStyle,
  wrapperAnimatedStyle,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const storeInputStyle = useUIStore(state => state.chatInputStyle);
  const messageWidth = useUIStore(state => state.messageWidth);
  const chatInputStyle = useMemo(() => {
    return forceInputStyle || storeInputStyle;
  }, [forceInputStyle, storeInputStyle]);
  const styles = useMemo(() => createStyles(isXS, chatInputStyle, messageWidth, colors),
    [isXS, chatInputStyle, messageWidth, colors]);

  return (
    <View style={styles.inputContainer}>
      <Animated.View style={[styles.inputWrapper, wrapperAnimatedStyle]}>
        {children}
      </Animated.View>
    </View>
  );
}

export default ChatInputContainer;
